import ERRORS, { ERROR_MESSAGES } from "./errors";

// fetch Headers only accept ISO-8859-1; smart quotes (e.g. U+2019) throw before request is sent
const toHeaderSafeValue = (value: string | null): string => {
  if (!value) return "";
  return value.replace(/[\u2018\u2019\u201A\u2032]/g, "'").replace(/[\u201C\u201D\u201E]/g, '"');
};

export const REQUEST_OPTIONS = (url = "", headers = {}) => ({
  headers: new Headers({
    Authorization: localStorage.getItem("authorization") || localStorage.getItem("auth"),
    "x-eq-auth-farm": toHeaderSafeValue(localStorage.getItem("farm")),
    "Content-Type": "application/json",
    ...headers,
  }),
});

// DONT send Content-Type Header when using fetch with FormData
const REQUEST_POST_OPTIONS = (url = "", headers = {}) => ({
  headers: new Headers({
    Authorization: localStorage.getItem("authorization") || localStorage.getItem("auth"),
    "x-eq-auth-farm": toHeaderSafeValue(localStorage.getItem("farm")),
    ...headers,
  }),
});

class Request {
  onExpireSession = () => {
    if (!window.location.pathname.includes("/sign-in/cover/")) {
      window.location.href = `${window.location.origin}/authentication/sign-in/cover/`;
    }

    this.onExpiredSessionHandler();
  };
  onExpiredSessionHandler = () => {
    // will be implemented in userActions.ts
  };
  setExpireSessionHandler(onExpireSession: () => void) {
    this.onExpiredSessionHandler = onExpireSession;
  }
  onServiceUnavailable = () => {
    //do nothing
  };
  setServiceUnavailableHandler(onServiceUnavailable: () => void) {
    this.onServiceUnavailable = onServiceUnavailable;
  }
  handleErrorStatuses = async (resp: Response) => {
    if (resp.status === ERRORS.UNAUTHORIZED.status) {
      let statusMessage;
      try {
        const respJson = await resp.clone().json();
        statusMessage = respJson?.statusMessage;
      } finally {
        this.onExpireSession();
        if (!statusMessage) {
          //No valid auth session is the default reason for 401
          //eslint-disable-next-line  no-unsafe-finally
          return resp;
        }
      }
    }

    if (
      resp.status === ERRORS.SERVICE_UNAVAILABLE.status ||
      resp.status === ERRORS.BAD_GATEWAY.status
    ) {
      this.onServiceUnavailable();
    }

    if (resp?.status?.toString()[0] !== "2") {
      let error = { status: resp.status, message: "", messages: {} };
      try {
        const err = await resp.clone().json();
        var errorMessage = err.message || err.error || err.errors || err.statusMessage;
        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage[0];
          error.message = errorMessage[0];
        } else {
          error.message = errorMessage || `${error.status} network error`;
        }

        error.messages = err?.messages;
      } catch {
        errorMessage = await resp.clone().text();
      } finally {
        errorMessage = errorMessage || `${error.status} network error`;
        if (error.status === ERRORS.BAD_REQUEST.status) {
          if (ERROR_MESSAGES.FORBIDDEN_ORGANIZATION.regexp.test(errorMessage)) {
            const org = errorMessage?.match(ERROR_MESSAGES.FORBIDDEN_ORGANIZATION.regexp)?.groups
              ?.organizationGroup;
            errorMessage = `${ERROR_MESSAGES.FORBIDDEN_ORGANIZATION.label} ${org || ""}`;
          }
        }
        //eslint-disable-next-line  no-unsafe-finally
        throw error;
      }
    }

    return resp;
  };
  get(url: string, signal: AbortSignal = undefined, options = {}) {
    return fetch(url, { ...REQUEST_OPTIONS(), ...options, signal }).then(this.handleErrorStatuses);
  }

  async getTextAsset(url: string, signal: AbortSignal) {
    const response = await this.get(url, signal);
    return response.clone().text();
  }

  async getJson(url: string, saveCache = false, signal: AbortSignal = undefined, options = {}) {
    try {
      const response = await this.get(url, signal, options).then(this.handleErrorStatuses);
      if (saveCache) {
        const cache = await caches.open("api");
        cache.put(url, response.clone());
      }
      return response.clone().json();
    } catch (e) {
      return Promise.reject(e);
    }
  }

  post(url: string, body: string, signal: AbortSignal, options = {}) {
    return fetch(url, {
      method: "POST",
      body: JSON.stringify(body),
      ...REQUEST_OPTIONS(url),
      ...options,
      signal,
    }).then(this.handleErrorStatuses);
  }

  postJson(url: string, body: string, signal: AbortSignal = undefined, options = {}) {
    return fetch(url, {
      method: "POST",
      body: body,
      ...REQUEST_OPTIONS(),
      ...options,
      signal,
    })
      .then(this.handleErrorStatuses)
      .then((resp) => resp.json());
  }

  putJson(url: string, body: string, signal: AbortSignal = undefined, options = {}) {
    return fetch(url, {
      method: "PUT",
      body: body,
      ...REQUEST_OPTIONS(),
      ...options,
      signal,
    })
      .then(this.handleErrorStatuses)
      .then((resp) => resp.json());
  }

  postAsText(url: string, body: string, signal: AbortSignal, options = {}) {
    return fetch(url, {
      method: "POST",
      body,
      ...REQUEST_OPTIONS("", { "Content-Type": "text/plain" }),
      ...options,
      signal,
    }).then(this.handleErrorStatuses);
  }

  postFormData(url: string, formData: FormData, signal: AbortSignal = undefined, options = {}) {
    return fetch(url, {
      method: "POST",
      body: formData,
      ...REQUEST_POST_OPTIONS("", {}),
      ...options,
      signal,
    }).then(this.handleErrorStatuses);
  }

  delete(url: string, signal: AbortSignal = undefined) {
    return fetch(url, {
      method: "DELETE",
      ...REQUEST_OPTIONS(),
      signal,
    }).then(this.handleErrorStatuses);
  }

  deleteJson(url: string, body: string, signal: AbortSignal = undefined) {
    return fetch(url, {
      method: "DELETE",
      body: JSON.stringify(body),
      ...REQUEST_OPTIONS(),
      signal,
    }).then(this.handleErrorStatuses);
  }

  async getJsonCache(url: string) {
    const cache = await caches.open("api");
    const cachedResponse = await cache.match(url);
    if (cachedResponse) {
      return cachedResponse.clone().json();
    }
    return null;
  }
}

const request = new Request();
export default request;
